import { RouteConfig } from 'vue-router';

import BenefitsHistory from '@/components/BenefitUsageHistory.vue';
import EntityHistory from '@/components/EntityHistory.vue';
import store from '@/store/store';
import { baseFilters } from '@/utils/baseFilters';
import ClientApplication from '@/views/client/Application.vue';
import ClientApplications from '@/views/client/Applications.vue';
import ClientView from '@/views/client/ClientView.vue';
import ClientProfileList from '@/views/client/List.vue';
import ClientProfile from '@/views/client/Profile.vue';
import ClientApplicationTab from '@/views/client/tabs/Applications.vue';
import clientCards from '@/views/client/tabs/Cards.vue';
import clientGroups from '@/views/client/tabs/Groups.vue';
import { tryParseBoolean } from '@witkac/utils/tryParseBoolean';

export const clientListRoute: RouteConfig = {
  path: 'clients',
  name: 'clientList',
  component: ClientProfileList,
  beforeEnter: (to, from, next) => {
    store.commit('breadcrumbs/set', [{ text: 'Mieszkańcy', disabled: true }]);
    store
      .dispatch('client/list', baseFilters(to))
      .then(() => next())
      .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać listy mieszkańców' }));
  },
  meta: { toolbarTitle: 'Wyszukaj klienta' }
};

export const clientDetailsRoute: RouteConfig = {
  path: 'clients/:id',
  component: ClientView,
  children: [
    {
      path: '',
      name: 'client',
      component: ClientProfile,
      beforeEnter: (to, from, next) => {
        store.commit('client/setId', to.params.id);

        store
          .dispatch('client/get')
          .then(res => {
            store.commit('breadcrumbs/set', [
              { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
              { text: res.firstName + ' ' + res.lastName, disabled: true }
            ]);

            next();
          })
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
      },
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },

    {
      path: 'usage',
      name: 'clientUsage',
      component: BenefitsHistory,
      beforeEnter: (to, from, next) => {
        store.commit('client/setId', to.params.id);

        store
          .dispatch('client/get')
          .then(res => {
            store.commit('breadcrumbs/set', [
              { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
              { text: res.firstName + ' ' + res.lastName, disabled: true }
            ]);
            store.dispatch('benefitsHistory/list', { ...baseFilters(to), profileId: res.id }).then(() => next());
          })
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
      },
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },
    {
      path: 'history',
      name: 'clientHistory',
      component: EntityHistory,
      beforeEnter: (to, from, next) => {
        store.commit('client/setId', to.params.id);
        Promise.all([
          store.dispatch('client/get').then(res =>
            store.commit('breadcrumbs/set', [
              { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
              { text: res.firstName + ' ' + res.lastName, disabled: true }
            ])
          ),
          store.dispatch('client/cards/list'),
          store.dispatch('entityHistory/list', {
            ...baseFilters(to),
            entityId: to.params.id,
            tableName: 'Profile',
            advanced: tryParseBoolean(to.query.advancedHistory, false)
          })
        ])
          .then(() => next())
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
      },
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },
    {
      path: 'groups',
      name: 'clientGroups',
      component: clientGroups,
      beforeEnter: (to, from, next) => {
        store.commit('client/setId', to.params.id);

        store
          .dispatch('client/get')
          .then(res => {
            store.commit('breadcrumbs/set', [
              { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
              { text: res.firstName + ' ' + res.lastName, disabled: true }
            ]);

            next();
          })
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
      },
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },
    {
      path: 'cards',
      name: 'clientCards',
      component: clientCards,
      beforeEnter: (to, from, next) => {
        store.commit('client/setId', to.params.id);

        store
          .dispatch('client/get')
          .then(res => {
            store.commit('breadcrumbs/set', [
              { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
              { text: res.firstName + ' ' + res.lastName, disabled: true }
            ]);

            next();
          })
          .catch(() => store.dispatch('notifyError', { title: 'Nie udało się pobrać danych klienta' }));
      },
      meta: {
        toolbarTitle: 'Profil klienta'
      }
    },
    {
      path: 'applications',
      component: ClientApplicationTab,
      children: [
        {
          path: '',
          name: 'clientApplications',
          component: ClientApplications,
          beforeEnter: (to, from, next) => {
            store.commit('client/setId', to.params.id);
            store
              .dispatch('client/get')
              .then(client => {
                return store
                  .dispatch(`application/list`, { ...baseFilters(to), profileId: client.id, withInactive: true })
                  .then(res => {
                    store.commit('breadcrumbs/set', [
                      { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
                      { text: client.firstName + ' ' + client.lastName, disabled: true },
                      { text: 'Wnioski', disabled: true }
                    ]);
                    next();
                  });
              })
              .catch(() =>
                store.dispatch('notifyError', {
                  title: 'Nie udało się pobrać danych klienta.'
                })
              );
          },
          meta: {
            toolbarTitle: 'Profil klienta'
          }
        },
        {
          path: ':applicationId',
          name: 'clientApplication',
          component: ClientApplication,
          beforeEnter: (to, from, next) => {
            store.commit('client/setId', to.params.id);
            store.commit('application/setId', parseInt(to.params.applicationId));
            Promise.all([store.dispatch('client/get'), store.dispatch('application/get')])
              .then(([client, application]) => {
                const isNewApplication = application.type === 'NEW';
                return store
                  .dispatch(
                    'settings/settingFind',
                    isNewApplication ? 'APPLICATION_NEW_GROUPS_FORM' : 'APPLICATION_EXTEND_GROUPS_FORM'
                  )
                  .then(() => ({ client, application }));
              })
              .then(({ client, application }) => {
                store.commit('breadcrumbs/set', [
                  { text: 'Mieszkańcy', to: { name: 'clientList' }, exact: true },
                  { text: client.firstName + ' ' + client.lastName, disabled: true },
                  { text: 'Wnioski', disabled: true },
                  { text: `#${application.id}`, disabled: true }
                ]);

                next();
              })
              .catch(() =>
                store.dispatch('notifyError', {
                  title: 'Nie udało się pobrać danych klienta'
                })
              );
          },
          meta: {
            toolbarTitle: 'Profil klienta'
          }
        }
      ]
    }
  ]
};
